import {
  Box,
  Flex,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useBoolean,
  useOutsideClick,
} from '@chakra-ui/react'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import downChevron from '~/assets/svgs/icon-down-chevron.svg?react'
import searchIcon from '~/assets/svgs/icon-search.svg?react'
import upChevron from '~/assets/svgs/icon-up-chevron.svg?react'
import { useDropdownStyles } from '~/components/shared/componentStyles/Dropdown.style'

import { Menu, MenuOptions } from './Menu'
import { MenuItem, MenuListItem } from './MenuItem'

export type SelectItem<TValue> = MenuItem<TValue> & {
  selectedLabel?: React.ReactNode
  searchLabel?: string
}

type DropdownProps<TValue extends string | number> = {
  placeholder?: string
  selectedIndex?: number
  selectedValue?: TValue
  items: SelectItem<TValue>[]
  onSelect?: (value: TValue, index: number) => string | void
  onScrollEndReached?: () => void
  menuOptions?: MenuOptions
  searchable?: boolean
}

export const DropdownSelect = <TValue extends string | number>({
  placeholder = '',
  selectedIndex,
  selectedValue,
  items = [],
  onSelect,
  onScrollEndReached,
  menuOptions,
  searchable,
}: DropdownProps<TValue>) => {
  const [internalIndex, setInternalIndex] = useState<number>()
  const [isOpen, setIsOpen] = useBoolean(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const styles = useDropdownStyles()

  useOutsideClick({
    ref: containerRef,
    handler: () => {
      if (isOpen) setIsOpen.off()
    },
  })

  const isSearchable = searchable !== undefined ? searchable : items.length > 10

  const extractTextFromReactNode = (node: React.ReactNode): string => {
    if (typeof node === 'string' || typeof node === 'number') {
      return node.toString()
    } else if (Array.isArray(node)) {
      return node.map(extractTextFromReactNode).join(' ')
    } else if (React.isValidElement(node)) {
      if (node.props.primaryText) {
        return node.props.primaryText
      } else if (node.props.children) {
        return extractTextFromReactNode(node.props.children)
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const handleSelectedInternal = (value: TValue, _index: number) => {
    const originalIndex = items.findIndex((item) => item.value === value)
    onSelect?.(value, originalIndex)
    setInternalIndex(originalIndex)
    setIsOpen.off()
    setSearchTerm('')
  }

  const handleMenuToggle = () => {
    setIsOpen.toggle()
    setSearchTerm('')
  }

  const getSelectedItemIndex = useCallback(
    (value?: TValue, index?: number) => {
      if (value !== undefined) {
        return items.findIndex((item) => item.value === value)
      }
      return index
    },
    [items],
  )

  const filteredItems = useMemo(() => {
    if (!isSearchable || searchTerm.trim() === '') {
      return items
    }
    return items.filter((item) => {
      const label = extractTextFromReactNode(item.content)
      return label.toLowerCase().includes(searchTerm.toLowerCase())
    })
  }, [items, searchTerm, isSearchable])

  const placeHolderText = useMemo(() => {
    return <Text sx={styles.placeholderText}>{placeholder}</Text>
  }, [placeholder])

  const SelectedText = useCallback(() => {
    const resolvedSelectedIndex = getSelectedItemIndex(
      selectedValue,
      selectedIndex ?? internalIndex,
    )
    if (resolvedSelectedIndex === undefined) return placeHolderText
    const selectedItem = items[resolvedSelectedIndex]
    if (!selectedItem) return placeHolderText
    let itemLabel = selectedItem.selectedLabel
    let itemContent = selectedItem.content
    if (typeof itemLabel === 'string') {
      itemLabel = (
        <Text noOfLines={1} sx={styles.defaultText}>
          {itemLabel}
        </Text>
      )
    }
    if (typeof itemContent === 'string') {
      itemContent = (
        <Text noOfLines={1} sx={styles.defaultText}>
          {itemContent}
        </Text>
      )
    }
    return itemLabel ?? itemContent ?? placeHolderText
  }, [items, selectedIndex, selectedValue, internalIndex])

  const wrappedItems = useMemo(() => {
    return filteredItems.map((item) => {
      if (
        typeof item.content === 'string' ||
        typeof item.content === 'number'
      ) {
        return {
          value: item.value,
          content: (
            <MenuListItem
              key={item.value}
              primaryText={item.content.toString()}
            />
          ),
        }
      }
      return {
        value: item.value,
        content: <MenuListItem key={item.value}>{item.content}</MenuListItem>,
      }
    })
  }, [filteredItems])

  return (
    <Flex ref={containerRef} sx={styles.container}>
      <Flex
        ref={dropdownRef}
        __css={styles.menuButton}
        onClick={handleMenuToggle}
      >
        <Box sx={{ px: 1 }}>
          <SelectedText />
        </Box>
        <Icon as={isOpen ? upChevron : downChevron} sx={styles.icon} />
      </Flex>
      {isOpen && (
        <Menu
          header={
            isSearchable ? (
              <InputGroup size="sm">
                <InputRightElement pointerEvents="none">
                  <Icon as={searchIcon} color="gray.400" boxSize={4} />
                </InputRightElement>
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </InputGroup>
            ) : undefined
          }
          anchorRef={dropdownRef}
          items={wrappedItems}
          onSelect={handleSelectedInternal}
          onScrollEndReached={onScrollEndReached}
          options={menuOptions}
        />
      )}
    </Flex>
  )
}
